import { ReactElement, SyntheticEvent, useMemo, useCallback } from 'react';
import moment from 'moment';

import currentTheme from '~config/theme';
import { getStartEndDates, getRangesDate } from '~utils/dates';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { pluralize } from '~utils/commons';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInWarehouseFiltersContext } from '../../../InWarehouseFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../PresetsContext';
import { DashboardVariantV2CarrierContextTabId } from '../../../types';
import {
  DashboardVariantV2CarrierStatusInfoWidgetCard,
  DashboardVariantV2CarrierStatusItemsList,
  DashboardVariantV2CarrierHeaderErrorAlert,
} from '../../SubComponents';
// eslint-disable-next-line max-len
import { DashboardVariantV2CarrierStatusItemsData } from '../../SubComponents/StatusItemsList/types';

const WIDGET_TAB_ID: DashboardVariantV2CarrierContextTabId = 'inWarehouse';
const WIDGET_ACCENT_COLOR = currentTheme.palette.quaternary.main;

const DashboardVariantV2CarrierInWarehouseWidgetCard = (): ReactElement => {
  const {
    data: {
      statistics: {
        data: {
          totalItems,
          inWarehouse: {
            totalInWarehouseItems,
            totalTodayInWarehouseItems,
            totalNextDaysInWarehouseItems,
            totalDelayedInWarehouseItems,
          },
        },
      },
      zoneSaturation: { reload: reloadZoneSaturation },
    },
    activeTab: { getIsActiveTab, setActiveTab },
  } = useDashboardVariantV2CarrierContext();

  const {
    filters: { handleClearFilters, handleClearFilterByValues },
    forceRequest: { toggleValue: toggleForceRequest },
  } = useDashboardVariantV2CarrierInWarehouseFiltersContext();

  const {
    selectedPreset: { reset: handleResetSelectedPreset },
  } = useDashboardVariantV2CarrierPresetsContext();

  const isSelected = useMemo(
    () => getIsActiveTab(WIDGET_TAB_ID),
    [getIsActiveTab],
  );

  const handleSelectedTab = useCallback(() => {
    if (!isSelected) {
      handleResetSelectedPreset();
    }

    setActiveTab(WIDGET_TAB_ID);
    reloadZoneSaturation({ withLoading: true, onlyTodayDelivery: false });
  }, [
    isSelected,
    setActiveTab,
    handleResetSelectedPreset,
    reloadZoneSaturation,
  ]);

  const handleSelectedTabByFilters = useCallback(
    (values: Record<string, unknown>, callback?: () => void) =>
      (event: SyntheticEvent) => {
        event.stopPropagation();

        handleClearFilters(false);
        handleClearFilterByValues(values, () => toggleForceRequest(isSelected));
        handleResetSelectedPreset();

        setActiveTab(WIDGET_TAB_ID);
        reloadZoneSaturation({ withLoading: true, onlyTodayDelivery: false });

        if (callback) {
          setTimeout(() => {
            callback();
          }, 150);
        }
      },
    [
      handleClearFilterByValues,
      handleResetSelectedPreset,
      isSelected,
      setActiveTab,
      toggleForceRequest,
      handleClearFilters,
      reloadZoneSaturation,
    ],
  );

  const handleSelectedTabWithExpiresToday = useCallback(
    (event: SyntheticEvent) => {
      const {
        startDate: expirationDateTimeFrom,
        endDate: expirationDateTimeTo,
      } = getStartEndDates({ unit: 'day' });

      handleSelectedTabByFilters({
        expirationDateTimeFrom,
        expirationDateTimeTo,
      })(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const handleSelectedTabWithExpiresCommingDays = useCallback(
    (event: SyntheticEvent) => {
      const nextDayDate = moment().add(1, 'day').toDate();

      const {
        startDate: expirationDateTimeFrom,
        endDate: expirationDateTimeTo,
      } = getRangesDate({
        initialDate: nextDayDate,
        end: { operation: 'add', amount: 1, unit: 'month' },
      });

      handleSelectedTabByFilters({
        expirationDateTimeFrom,
        expirationDateTimeTo,
        includeWithoutExpirationDate: true,
      })(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const handleSelectedTabWithDelayedExpiration = useCallback(
    (event: SyntheticEvent) => {
      const previousDayDate = moment().subtract(1, 'day').toDate();

      const {
        startDate: expirationDateTimeFrom,
        endDate: expirationDateTimeTo,
      } = getRangesDate({
        initialDate: previousDayDate,
        start: { operation: 'subtract', amount: 1, unit: 'month' },
      });

      handleSelectedTabByFilters({
        expirationDateTimeFrom,
        expirationDateTimeTo,
      })(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const hasDelayedItems = useMemo(
    () => totalDelayedInWarehouseItems > 0,
    [totalDelayedInWarehouseItems],
  );

  const delayedItemText = useMemo(
    () =>
      pluralize({
        singular: 'Envío demorado',
        plural: 'Envíos demorados',
        count: totalDelayedInWarehouseItems,
        includeCount: true,
      }),
    [totalDelayedInWarehouseItems],
  );

  const statusItems = useMemo<
    DashboardVariantV2CarrierStatusItemsData[]
  >(() => {
    const defaultItems: DashboardVariantV2CarrierStatusItemsData[] = [
      {
        label: 'Vencen hoy',
        labelBold: true,
        value: totalTodayInWarehouseItems,
        onClick: handleSelectedTabWithExpiresToday,
      },
      {
        label: 'Vencen en próximos días',
        value: totalNextDaysInWarehouseItems,
        onClick: handleSelectedTabWithExpiresCommingDays,
      },
    ];

    const delayedItem: DashboardVariantV2CarrierStatusItemsData = {
      label: 'Envíos demorados',
      value: totalDelayedInWarehouseItems,
      onClick: handleSelectedTabWithDelayedExpiration,
    };

    if (!hasDelayedItems) return [...defaultItems, delayedItem];

    return defaultItems;
  }, [
    handleSelectedTabWithDelayedExpiration,
    handleSelectedTabWithExpiresCommingDays,
    handleSelectedTabWithExpiresToday,
    hasDelayedItems,
    totalDelayedInWarehouseItems,
    totalNextDaysInWarehouseItems,
    totalTodayInWarehouseItems,
  ]);

  const footer = useMemo(() => {
    if (hasDelayedItems) {
      return (
        <DashboardVariantV2CarrierHeaderErrorAlert
          label={delayedItemText}
          value={totalDelayedInWarehouseItems}
          onClick={handleSelectedTabWithDelayedExpiration}
        />
      );
    }

    return undefined;
  }, [
    delayedItemText,
    handleSelectedTabWithDelayedExpiration,
    hasDelayedItems,
    totalDelayedInWarehouseItems,
  ]);

  return (
    <DashboardVariantV2CarrierStatusInfoWidgetCard
      accentColor={WIDGET_ACCENT_COLOR}
      isSelected={isSelected}
      onSelected={handleSelectedTab}
      value={{
        current: totalInWarehouseItems,
        total: totalItems,
      }}
      title={{
        text: 'EN DEPÓSITO',
        tooltip:
          'Gestión de pedidos que se encuentran en el depósito del transportista.',
      }}
      icon={{
        src: toAbsoluteUrl('/icons/icon_section_warehouse.svg'),
        alt: 'Section Warehouse Icon',
      }}
      alert={{
        show: hasDelayedItems,
        tooltip: delayedItemText,
        severity: 'error',
        onClick: handleSelectedTabWithDelayedExpiration,
      }}
      content={<DashboardVariantV2CarrierStatusItemsList items={statusItems} />}
      footer={footer}
    />
  );
};

export default DashboardVariantV2CarrierInWarehouseWidgetCard;
